import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'

type PraktikKinerjaStepCardProps = {
  className?: string
  iconSrc: string
  status: StepCardStatus
  title: string | React.ReactNode
  description?: string | React.ReactNode
  actions?: React.ReactNode
  lockedHint?: string
}

export type StepCardStatus = 'active' | 'locked' | 'completed'

export function PraktikKinerjaStepCard({
  actions,
  className,
  description,
  iconSrc,
  lockedHint,
  status,
  title,
}: PraktikKinerjaStepCardProps) {
  return (
    <div
      className={clsx(
        'flex-1 min-w-0 border rounded-lg bg-white p-6 space-y-4',
        status === 'active' ? 'border-interactive-default' : 'border-subdued',
        className
      )}
    >
      <div className="flex justify-between items-start">
        <img
          src={assetUrl(iconSrc)}
          alt=""
          width={64}
          height={64}
          className={clsx('flex-shrink-0', status === 'locked' && 'grayscale')}
        />
        <StatusIcon status={status} />
      </div>

      <div className="flex flex-col space-y-1 flex-1">
        <Text variant="heading-sm" as="h3">
          {title}
        </Text>
        {description && (
          <Text variant="body" color="subdued">
            {description}
          </Text>
        )}
      </div>

      {status === 'locked' && lockedHint ? (
        <div className="text-blue-40 flex items-center gap-1 mt-7">
          <Icon color="informational" fontSize="sm" className="flex-shrink-0">
            lock
          </Icon>
          <Text variant="body-sm" color="informational">
            {lockedHint}
          </Text>
        </div>
      ) : (
        actions && <div className="mt-4">{actions}</div>
      )}
    </div>
  )
}

const STATUS_ICON_PROPS: Record<
  StepCardStatus,
  { icon: string; className: string }
> = {
  active: {
    icon: 'lock_open_outlined',
    className: '!text-blue-40',
  },
  locked: {
    icon: 'lock_outlined',
    className: '!text-gray-30',
  },
  completed: {
    icon: 'check_circle_outlined',
    className: '!text-green-50',
  },
}

function StatusIcon({ status }: { status: StepCardStatus }) {
  const resolvedIconProps = STATUS_ICON_PROPS[status]

  if (!resolvedIconProps) return null

  return (
    <Icon
      fontSize="lg"
      className={clsx(
        'pointer-events-none flex-shrink-0',
        /*
         * Note: we need to explicitly set font-size and line-height as workaround of styles collision
         * due to multiple version of @wartek-id/icon being used in this project.
         */
        '!text-4xl !leading-9',
        resolvedIconProps.className
      )}
    >
      {resolvedIconProps.icon}
    </Icon>
  )
}
